import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './WelcomeModal.module.scss';

type Props = {
  onDismiss: () => void;
};

const WelcomeModal: FC<Props> = ({ onDismiss }) => {
  const [t] = useTranslation('dashboard');
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onDismiss]);

  return (
    <div
      className={styles.overlay}
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onDismiss(); }}
    >
      <div className={styles.card}>
        <button className={styles.closeBtn} onClick={onDismiss} aria-label={t('inbox.welcome.close', 'Stäng')}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </button>

        <div className={styles.visual}>
          <InboxIllustration />
        </div>

        <div className={styles.body}>
          <span className={styles.chip}>{t('inbox.welcome.chip', 'Nyhet')}</span>
          <h2 className={styles.title}>{t('inbox.welcome.title', 'Inkorg')}</h2>
          <p className={styles.lead}>
            {t('inbox.welcome.lead', 'Alla meddelanden och anbud från leverantörer samlade på ett ställe — du slipper gå in i varje projekt separat för att hålla koll på svaren.')}
          </p>
          <ul className={styles.list}>
            <li>{t('inbox.welcome.list.messages', 'Meddelanden och anbud från leverantörer i alla projekt')}</li>
            <li>{t('inbox.welcome.list.unread', 'Olästa konversationer markerade med blått')}</li>
            <li>{t('inbox.welcome.list.reply', 'Svara direkt utan att lämna inkorgen')}</li>
            <li>{t('inbox.welcome.list.filter', 'Filtrera på projekt, typ eller sökord')}</li>
            <li>{t('inbox.welcome.list.activity', 'Skapa en aktivitet direkt från ett meddelande')}</li>
          </ul>
          <button className={styles.cta} onClick={onDismiss}>
            {t('inbox.welcome.cta', 'Kom igång')}
          </button>
        </div>
      </div>
    </div>
  );
};

const InboxIllustration: FC = () => (
  <svg viewBox="0 0 440 160" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    {/* Row 1 — unread, indigo avatar */}
    <rect x="12" y="10" width="416" height="42" rx="8" fill="white" opacity="0.9"/>
    <circle cx="34" cy="31" r="14" fill="#6366F1"/>
    <rect x="60" y="19" width="120" height="8" rx="4" fill="#374151"/>
    <rect x="60" y="33" width="160" height="6" rx="3" fill="#9CA3AF"/>
    <rect x="380" y="19" width="34" height="6" rx="3" fill="#D1D5DB"/>
    <circle cx="414" cy="33" r="5" fill="#3B82F6"/>

    {/* Row 2 — unread, pink avatar */}
    <rect x="12" y="59" width="416" height="42" rx="8" fill="white" opacity="0.9"/>
    <circle cx="34" cy="80" r="14" fill="#EC4899"/>
    <rect x="60" y="68" width="100" height="8" rx="4" fill="#374151"/>
    <rect x="60" y="82" width="140" height="6" rx="3" fill="#9CA3AF"/>
    <rect x="380" y="68" width="34" height="6" rx="3" fill="#D1D5DB"/>
    <circle cx="414" cy="82" r="5" fill="#3B82F6"/>

    {/* Row 3 — read, muted teal avatar */}
    <rect x="12" y="108" width="416" height="42" rx="8" fill="white" opacity="0.6"/>
    <circle cx="34" cy="129" r="14" fill="#06B6D4" opacity="0.5"/>
    <rect x="60" y="117" width="110" height="8" rx="4" fill="#9CA3AF"/>
    <rect x="60" y="131" width="130" height="6" rx="3" fill="#D1D5DB"/>
    <rect x="380" y="117" width="34" height="6" rx="3" fill="#E5E7EB"/>
  </svg>
);

export default WelcomeModal;
